import { type FunctionComponent, memo, useContext } from "react";
import { useDispatch } from "react-redux";
import { useFragment } from "@apollo/client/react";
import type { ViewLink } from "@ppb/tbd-store/state/layout/views/ViewLink.types";
import { PUSH } from "@ppb/tbd-store/actions/router";
import { MY_BETS_RESET_FILTERS } from "@ppb/tbd-store/actions/my-bets";
import PebbleCardGroupContainer from "@ppb/tbd-components-sports-betting/components/PebbleCardGroup/view/PebbleCardGroup.web";
import { PebbleCardGroupFragment } from "@ppb/tbd-components-sports-betting/components/PebbleCardGroup/model/PebbleCardGroup.graphql";

import PebbleContent, { contentSignalsReady } from "./PebbleContent/PebbleContent.web";
import ConnectedFavouriteIcon from "../FavouriteIcon";
import FavouriteIcon from "../FavouriteIcon/FavouriteIcon.web";
import { OpenBets } from "../OpenBets/OpenBets.web";
import { isCardImplemented } from "../Card/Card.web";
import { ConfigContext } from "../Config/ConfigContext";
import { useSelectedCardFetch } from "./useSelectedCardFetch";
import { useUndeliveredCardUrns } from "../../hooks/useUndeliveredCardUrns";
import { useIsPebbleAvailable } from "./useIsPebbleAvailable";
import { useSuperSubExperiment } from "./useSuperSubExperiment";

// ConnectedOpenBets resolves its link from the pebblecardgroups Redux slice, which this group does not
// populate. Read viewOpenBets from the Apollo cache instead and dispatch navigation from the host.
const OpenBetsSlot: FunctionComponent<{ cardGroupUrn: string }> = ({ cardGroupUrn }) => {
  const dispatch = useDispatch();
  // fragmentName is required, not decoration: the package's document defines several fragments.
  const { data } = useFragment({
    fragment: PebbleCardGroupFragment,
    fragmentName: "PebbleCardGroup",
    from: { __typename: "PebbleCardGroup", urn: cardGroupUrn },
  });
  // Built field by field rather than cast: useFragment returns deep-partial data, so asserting the whole
  // object would let a partially cached group reach navigation with an undefined url.
  const { viewUrl, viewUrn, navigationIntent } = data?.viewOpenBets ?? {};
  // navigationIntent replaces the deprecated viewDisplayMode (THISISF-1041). The package's codegen emits
  // string unions where the host's emits TS enums; identical at runtime, hence the cast.
  const viewLink: ViewLink | undefined =
    viewUrl && viewUrn
      ? { viewUrl, viewUrn, navigationIntent: navigationIntent as ViewLink["navigationIntent"] }
      : undefined;

  return (
    <OpenBets
      cardURN={cardGroupUrn}
      viewLink={viewLink}
      dispatchOpenBetsNavigation={(link) => {
        dispatch({ type: PUSH, payload: link });
        dispatch({ type: MY_BETS_RESET_FILTERS });
      }}
      // Required by the shared props but never called on web: OpenBets.web resets through the navigation
      // callback above, and only OpenBets.native calls this one.
      dispatchResetFilters={() => undefined}
    />
  );
};

// Host adapter for the package's PebbleCardGroup container, registered in migratedCardWhiteList
// (Card.web.tsx): it fills the container's host slots with tbd pieces the package cannot import.
const PebbleCardGroup: FunctionComponent<{ urn: string; visible?: boolean }> = ({ urn, visible = true }) => {
  const { isDesktopLayout } = useContext(ConfigContext);
  const isPebbleAvailable = useIsPebbleAvailable();

  // The inner card is fetched here, on the always-rendered component, not in the content slot: that slot
  // only mounts when the group is expanded, so fetching there leaves a collapsed group with no content.
  // Filtered by the container's own predicate, so both resolve the same selection.
  useSelectedCardFetch(urn, isCardImplemented, isPebbleAvailable);

  const isSuperSubExperimentActive = useSuperSubExperiment();
  const undeliveredCardUrns = useUndeliveredCardUrns();

  // The lazy loader asks for this group a few cards ahead of the viewport, so a group the BFF has nothing
  // for is already known to be empty before it reaches the screen. Skipping it here keeps that decision
  // off-screen; leaving it to the container's own query means painting a header and then removing it
  // mid-scroll, which shifts everything below.
  if (undeliveredCardUrns.includes(urn)) {
    return null;
  }

  return (
    <PebbleCardGroupContainer
      urn={urn}
      visible={visible}
      isDesktopLayout={isDesktopLayout}
      isSupportedTypename={isCardImplemented}
      isPebbleAvailable={isPebbleAvailable}
      isSuperSubExperimentActive={isSuperSubExperimentActive}
      contentSignalsReady={contentSignalsReady}
      renderContent={({ urn: contentUrn, typename, visible: contentVisible, onReady, outerCardRef }) => (
        <PebbleContent
          urn={contentUrn}
          typename={typename}
          visible={contentVisible}
          onReady={onReady}
          outerCardRef={outerCardRef}
          isSuperSubExperimentActive={isSuperSubExperimentActive}
        />
      )}
      // One element per slot: riding OpenBets in here tied the link to the star, which the package drops
      // whenever BFF omits `favouriteMarketsState` - i.e. with the FAVOURITE_MARKETS throttle off.
      renderFavouriteIcon={({ favouriteMarketsStateUrn, cardGroupUrn }) => (
        <ConnectedFavouriteIcon
          component={FavouriteIcon}
          urn={favouriteMarketsStateUrn}
          contentSectionURN={cardGroupUrn}
        />
      )}
      // OpenBets reads `viewOpenBets` itself and shows the disabled label when there is none - which only
      // reaches the screen once the package stops gating this slot on that same field.
      renderOpenBets={({ cardGroupUrn }) => <OpenBetsSlot cardGroupUrn={cardGroupUrn} />}
    />
  );
};

// Memoised at the boundary the dispatcher renders, which is what INCGNT-702 did to the legacy component:
// both props are primitives, so a parent re-render costs nothing unless the urn or visibility changed.
export default memo(PebbleCardGroup);
